import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ohif/ui-next';
import FormField from './FormField';
import Button from '../Button';
import { validateChangePassword, type ChangePasswordFormValues } from '../../validation';
import type { ChangePasswordModalProps } from '../../types/modal.types';
import { Z_INDEX_CHANGE_PASSWORD_MODAL } from '../../constants';

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title = 'Change Password',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined' || !isOpen) {
      return;
    }
    const rafId = requestAnimationFrame(() => {
      const content = document.querySelector(
        '.change-password-dialog-content'
      ) as HTMLElement | null;
      if (content) {
        content.style.setProperty('z-index', String(Z_INDEX_CHANGE_PASSWORD_MODAL), 'important');
        const overlay = content.previousElementSibling as HTMLElement | null;
        if (overlay) {
          overlay.style.setProperty('z-index', String(Z_INDEX_CHANGE_PASSWORD_MODAL), 'important');
        }
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [isOpen]);

  const handleSubmit = async (values: ChangePasswordFormValues) => {
    setIsSubmitting(true);
    try {
      await onSubmit(values.newPassword);
      onClose();
    } catch (error) {
      console.error('Error changing password:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => !open && !isSubmitting && onClose()}
      shouldCloseOnEsc={!isSubmitting}
      shouldCloseOnOverlayClick={false}
    >
      <DialogContent className="change-password-dialog-content">
        {title && (
          <DialogHeader>
            <DialogTitle className="create-user-dialog-title">{title}</DialogTitle>
          </DialogHeader>
        )}
        <Formik
          initialValues={{
            newPassword: '',
            confirmPassword: '',
          }}
          validate={validateChangePassword}
          onSubmit={handleSubmit}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ values, errors, touched, handleChange, handleSubmit, isValid }) => {
            const getFieldError = (fieldName: string) => {
              const error = errors[fieldName as keyof typeof errors];
              const isTouched = touched[fieldName as keyof typeof touched];
              const fieldValue = values[fieldName as keyof typeof values];
              return (isTouched || fieldValue) && error ? error : undefined;
            };
            return (
              <form onSubmit={handleSubmit}>
                <div className="change-password-dialog-form-body">
                  <FormField
                    label="New Password"
                    name="newPassword"
                    type="password"
                    required
                    value={values.newPassword}
                    onChange={handleChange}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    error={getFieldError('newPassword')}
                  />
                  <FormField
                    label="Confirm Password"
                    name="confirmPassword"
                    type="password"
                    required
                    value={values.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    error={getFieldError('confirmPassword')}
                  />
                </div>
                <div className="change-password-dialog-footer">
                  <Button
                    type="button"
                    variant="secondary"
                    fullWidth={false}
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="create-user-dialog-btn create-user-dialog-btn-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth={false}
                    disabled={!isValid || isSubmitting}
                    className="create-user-dialog-btn create-user-dialog-btn-primary"
                  >
                    {isSubmitting ? 'Updating...' : 'Update Password'}
                  </Button>
                </div>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordModal;
